import * as THREE from "three";

/**
 * Tworzy efekt eksplozji lasera w podanej pozycji
 * @param scene Scena Three.js
 * @param position Pozycja eksplozji
 * @param scale Skala eksplozji (domyślnie 1.0)
 * @returns THREE.Group zawierający wszystkie elementy eksplozji
 */
export function createLaserExplosion(
  scene: THREE.Scene, 
  position: THREE.Vector3,
  scale: number = 1.0
): THREE.Group {
  // Kontener na wszystkie elementy eksplozji
  const explosionGroup = new THREE.Group();
  explosionGroup.position.copy(position);
  // Flaga do śledzenia czy eksplozja jest aktywna
  explosionGroup.userData.isActive = true;
  scene.add(explosionGroup);

  try {
    // Efekt eksplozji - główne światło (intensywne czerwone)
    const explosionLight = new THREE.PointLight(0xff3030, 7 * scale, 15 * scale);
    explosionGroup.add(explosionLight);

    // Dodatkowe światło pulsujące (jaśniejsze, bardziej pomarańczowe)
    const pulseLight = new THREE.PointLight(0xff6030, 4 * scale, 10 * scale);
    explosionGroup.add(pulseLight);

    // Efekt eksplozji - cząsteczki lasera
    const particleCount = Math.floor(30 * scale);
    const particles: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[] = [];

    // Tworzymy cząsteczki lasera
    for (let i = 0; i < particleCount; i++) {
      const particleGeometry = new THREE.SphereGeometry(
        (0.03 + Math.random() * 0.1) * scale,
        8,
        8
      );
      
      // Losowy odcień czerwonego/pomarańczowego dla cząsteczek
      const redIntensity = 0.8 + Math.random() * 0.2;
      const particleMaterial = new THREE.MeshBasicMaterial({
        color: new THREE.Color(
          redIntensity, // Pełny czerwony
          0.2 + Math.random() * 0.4, // Trochę zielonego dla pomarańczowego
          0.1 + Math.random() * 0.2  // Minimalnie niebieskiego
        ),
        transparent: true,
        opacity: 0.9,
      });
      const particle = new THREE.Mesh(particleGeometry, particleMaterial);

      // Losowy kierunek rozrzutu z większym rozproszeniem
      const direction = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 0.5, // Większy rozrzut w górę
        Math.random() * 2 - 1
      ).normalize();

      // Zapisz kierunek i prędkość jako właściwości cząsteczki
      // Cząsteczki lasera poruszają się szybciej i bardziej chaotycznie
      particle.userData = { 
        direction, 
        speed: (0.1 + Math.random() * 0.2) * scale,
        phase: Math.random() * Math.PI * 2 // Faza do efektu migotania
      };

      explosionGroup.add(particle);
      particles.push(particle);
    }

    // Dodajemy efekt "promieni" lasera
    const laserRayCount = Math.floor(8 * scale); // Więcej promieni niż w błyskawicy
    const laserRays: THREE.Mesh<THREE.BoxGeometry, THREE.MeshBasicMaterial>[] = [];
    
    for (let i = 0; i < laserRayCount; i++) {
      // Tworzymy "promień" lasera jako wydłużony prostopadłościan
      const rayLength = (1.2 + Math.random() * 2.5) * scale;
      const rayGeometry = new THREE.BoxGeometry(0.04 * scale, rayLength, 0.04 * scale);
      const rayMaterial = new THREE.MeshBasicMaterial({
        color: 0xff4040, // Intensywny czerwony
        transparent: true,
        opacity: 0.8,
      });
      const ray = new THREE.Mesh(rayGeometry, rayMaterial);
      
      // Losowy kierunek promienia
      const direction = new THREE.Vector3(
        Math.random() * 2 - 1,
        Math.random() * 2 - 1,
        Math.random() * 2 - 1
      ).normalize();
      
      // Ustawiamy orientację promienia zgodnie z kierunkiem
      ray.position.set(direction.x * rayLength/2, direction.y * rayLength/2, direction.z * rayLength/2);
      ray.lookAt(new THREE.Vector3(0, 0, 0));
      
      // Zapisujemy dane do animacji
      ray.userData = {
        initialLength: rayLength,
        direction: direction
      };
      
      explosionGroup.add(ray);
      laserRays.push(ray);
    }

    // Animacja eksplozji lasera
    let explosionTime = 0;
    let animationFrameId: number;
    const animateExplosion = () => {
      // Sprawdź czy grupa eksplozji jest nadal w scenie i aktywna
      if (!explosionGroup.parent || !explosionGroup.userData.isActive) {
        // Cleanup jeśli usunięto ze sceny
        cancelAnimationFrame(animationFrameId);
        cleanupExplosion();
        return;
      }

      explosionTime += 0.016;

      // Zmniejszaj intensywność głównego światła
      explosionLight.intensity = Math.max(0, 7 * scale - explosionTime * 7);
      
      // Pulsujące światło
      pulseLight.intensity = Math.max(0, 4 * scale - explosionTime * 4) * 
                            (0.7 + 0.3 * Math.sin(explosionTime * 35)); // Szybsze pulsowanie

      // Aktualizuj pozycje cząsteczek
      for (const particle of particles) {
        const dir = particle.userData.direction as THREE.Vector3;
        const spd = particle.userData.speed as number;
        const phase = particle.userData.phase as number;

        particle.position.add(
          new THREE.Vector3(
            dir.x * spd,
            dir.y * spd - 0.01 * scale, // Większa grawitacja dla efektu lasera
            dir.z * spd
          )
        );

        // Efekt migotania cząsteczek
        const flickerFactor = 0.7 + 0.3 * Math.sin(explosionTime * 25 + phase); // Szybsze migotanie
        (particle.material as THREE.MeshBasicMaterial).opacity = Math.max(
          0,
          0.9 * flickerFactor - explosionTime * 1.1 // Szybsze zanikanie
        );
        
        // Losowa zmiana rozmiaru dla efektu migotania
        particle.scale.set(
          1.0 + 0.3 * Math.sin(explosionTime * 20 + phase),
          1.0 + 0.3 * Math.sin(explosionTime * 20 + phase),
          1.0 + 0.3 * Math.sin(explosionTime * 20 + phase)
        );
      }
      
      // Animacja promieni lasera
      for (const ray of laserRays) {
        // Zmniejszanie długości promieni z czasem
        const initialLength = ray.userData.initialLength as number;
        const newLength = Math.max(0, initialLength * (1 - explosionTime * 1.2)); // Szybsze zanikanie
        
        // Aktualizacja geometrii promienia
        ray.scale.y = newLength / initialLength;
        
        // Migotanie promieni
        (ray.material as THREE.MeshBasicMaterial).opacity = Math.max(
          0,
          0.8 - explosionTime * 0.9 // Szybsze zanikanie
        ) * (0.7 + 0.3 * Math.sin(explosionTime * 30 + Math.random())); // Szybsze migotanie
      }

      // Zakończ animację po 0.8 sekundy (krócej niż błyskawica)
      if (explosionTime > 0.8) {
        // Anuluj animację
        cancelAnimationFrame(animationFrameId);
        cleanupExplosion();
        return;
      }
      animationFrameId = requestAnimationFrame(animateExplosion);
    };

    // Funkcja czyszcząca
    const cleanupExplosion = () => {
      explosionGroup.userData.isActive = false;
      // Usuń wszystkie elementy z grupy
      while (explosionGroup.children.length > 0) {
        const child = explosionGroup.children[0];
        explosionGroup.remove(child);
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose();
          child.material.dispose();
        } else if (child instanceof THREE.Light) {
          // Cleanup zasobów światła jeśli potrzebne
        }
      }
      // Usuń grupę ze sceny jeśli nadal tam jest
      if (explosionGroup.parent) {
        scene.remove(explosionGroup);
      }
    };

    // Dodaj metodę czyszczącą do grupy do użytku zewnętrznego
    explosionGroup.userData.cleanup = cleanupExplosion;
    
    // Rozpocznij animację
    animationFrameId = requestAnimationFrame(animateExplosion);
    
    return explosionGroup;
  } catch (error) {
    console.error("Error creating laser explosion:", error);
    // W przypadku błędu, usuń grupę ze sceny i zwróć pustą grupę
    scene.remove(explosionGroup);
    return new THREE.Group();
  }
}